// The capability sets the matrix offers: the abilities of its capability
// axis, as the columns (or, against conditions and modes, the rows). The ids
// are the backbone's stable ids, the same in every graph build; an id a graph
// does not know is left out by the backend. The names are only comments: the
// table names each ability as the graph does.

// The effect area of the abilities that say little as columns ("operate",
// "withstand"): the default set leaves them out
export const GENERAL_AREA_ID = "cpt__d8f8cf64-0887-55ce-8bd6-2f0e2477feb3";

// The set of the abilities the selection claims most, the general ones left out
export const MOST_CLAIMED = "claimed";

// A set the user put together, its abilities in the address
export const CUSTOM = "custom";

export interface CapabilityPreset {
  id: string;
  label: string;
  abilities: readonly string[];
}

export const CAPABILITY_PRESETS: readonly CapabilityPreset[] = [
  {
    id: "missions",
    label: "Missions",
    abilities: [
      "cpt__33d87b72-a66c-5b60-bf8d-f9c41e536185", // strike targets
      "cpt__41cef189-3726-5a0f-9f56-3b01dc0d2cb9", // reconnoitre areas
      "cpt__84b5f314-6720-5b91-a2ee-178bf4807a1f", // surveil areas
      "cpt__a172e0c3-6d95-5f86-8fe0-5ba572f8819c", // counter air threats
      "cpt__28567439-5437-5ce3-b79c-e1e5d2363b1c", // survey terrain
      "cpt__26273a8d-c8c1-5ed8-b7da-9b3a8e2c4d8e", // transport cargo
      "cpt__c2d384e6-7826-5529-8274-df5e14bfa341", // clear mines
      "cpt__32274067-2757-5103-8a10-3dcbe2c58324", // collect intelligence
      "cpt__ad86800d-42fe-5ee1-8544-c9bbd8036c2c", // conduct ISR
      "cpt__645a83aa-bbc0-5428-a37a-575519a6de3a", // evacuate people
      "cpt__d53a1cb3-f8bd-5d75-912b-52999472cdc3", // conduct electronic warfare
      "cpt__e15f27c7-8fe2-537b-a20b-35ffe8a2cc00", // inspect assets
      "cpt__34577fdf-7bc5-5577-ae8d-e7e04cf8f214", // patrol areas
      "cpt__5fe68d07-cbba-597a-8f06-df1f9293c990", // protect sites and people
      "cpt__41c246a8-9556-54fe-b1b3-aa5e474ffc8c", // rescue people
      "cpt__1629b383-ba01-5a23-be3a-ab7981400853", // lay mines
      "cpt__0f6d6790-4cdf-5bd5-a003-d5fb05c800e0", // counter submarines
      "cpt__63ab7554-8fc2-5990-8bed-c44ccb226fca", // resupply forces
      "cpt__d294b511-084a-5b7a-bf54-f434bec60332", // penetrate air defences
    ],
  },
  {
    id: "navigation",
    label: "Navigation & timing",
    abilities: [
      "cpt__f1548e0d-5beb-5f26-8c3d-7966bd4dff5c", // navigate
      "cpt__cc689ebc-9354-5d5a-ab2d-e6be42ef1b82", // determine position
      "cpt__73f3396a-1c97-5f5a-9229-889f6fe5e1bc", // mitigate interference
      "cpt__e6118ceb-f12d-58bb-8985-88915b001818", // determine attitude and heading
      "cpt__521b3555-e531-51fa-98ba-a1601195602d", // receive navigation signals
      "cpt__1253dc14-3ebc-5f2f-a020-1484be92865b", // keep time
      "cpt__0d9d84f6-8a1e-5ca3-8a9d-cfb20354ae73", // determine range or altitude
      "cpt__3780f572-559a-55f2-a0fa-f890d7ba0797", // measure velocity
      "cpt__cbccc049-d780-58d5-8ec5-cdce819731a9", // measure own motion
      "cpt__e6ecdfcb-e87b-57b4-a0e1-2bd82b19dfaa", // synchronise time
      "cpt__636594be-e720-548a-bc33-50a2acff7008", // measure angular rate
      "cpt__fe648356-62c2-5345-93fa-9077090b5683", // compensate navigation error
    ],
  },
  {
    id: "strike",
    label: "Strike & effectors",
    abilities: [
      "cpt__33d87b72-a66c-5b60-bf8d-f9c41e536185", // strike targets
      "cpt__f6ce511d-abcc-52a2-9dc3-07f4aea2e865", // launch munitions or aircraft
      "cpt__8af705ca-37b2-57d2-a86e-04e88ea4d9ef", // guide
      "cpt__2dfa7d8d-c740-5c14-bbec-7e80e6cb5eae", // fire projectiles
      "cpt__413a2d4c-47cc-58bf-a335-4659266240f2", // deliver weapon
      "cpt__e8e29400-2a30-53c8-afad-ded63209fdf3", // acquire target
      "cpt__bcc384c8-68e9-5c4c-9f75-2c959aea4fac", // loiter
      "cpt__d61fd0d7-96b5-58c1-a9f8-d5286455fdda", // detonate
      "cpt__d294b511-084a-5b7a-bf54-f434bec60332", // penetrate air defences
      "cpt__66be8df8-7601-5c0e-87cd-15efbf655b6f", // aim
      "cpt__7cb9ec49-02b6-54dc-bce6-ec4dcd174c70", // penetrate hardened targets
    ],
  },
  {
    id: "isr",
    label: "ISR & sensing",
    abilities: [
      "cpt__f29a8c63-3500-5a2d-a2ce-40ee41e53bcb", // detect
      "cpt__269ae338-92b8-57ee-8893-7c0b8168dadd", // track
      "cpt__41cef189-3726-5a0f-9f56-3b01dc0d2cb9", // reconnoitre areas
      "cpt__84b5f314-6720-5b91-a2ee-178bf4807a1f", // surveil areas
      "cpt__cd458527-0412-5cde-b244-18d69d646a0c", // image
      "cpt__49fd7eeb-7955-5f35-b150-ee12afdc822f", // recognise
      "cpt__32274067-2757-5103-8a10-3dcbe2c58324", // collect intelligence
      "cpt__ad86800d-42fe-5ee1-8544-c9bbd8036c2c", // conduct ISR
      "cpt__8dd25d18-27e8-586d-b985-b42f16184d5b", // fuse data
      "cpt__bc0dfcb9-cadf-5ba4-95a3-1ad4040b3956", // map surroundings
      "cpt__f0a75876-6fec-578e-9fd7-193ccadd6a79", // perceive
    ],
  },
  {
    id: "ew",
    label: "EW & resilience",
    abilities: [
      "cpt__73f3396a-1c97-5f5a-9229-889f6fe5e1bc", // mitigate interference
      "cpt__42b6828e-8a52-539c-94fc-809d6fa6ff42", // jam signals
      "cpt__d53a1cb3-f8bd-5d75-912b-52999472cdc3", // conduct electronic warfare
      "cpt__e368186d-05fb-53c3-9842-614ace5fdde9", // reduce signature
      "cpt__88712265-da2b-5eea-a877-6380d55cb43d", // authenticate
      "cpt__46f75d58-3126-5178-afd5-9fdcf7974398", // spoof signals
      "cpt__6b3efd31-4ef3-5deb-aa8b-fe3c2c2f74ea", // encrypt and decrypt
    ],
  },
  {
    id: "autonomy",
    label: "Autonomy & control",
    abilities: [
      "cpt__83d1411e-fcfa-54b8-86d6-5b7a156b831a", // control
      "cpt__8af705ca-37b2-57d2-a86e-04e88ea4d9ef", // guide
      "cpt__7da9bb83-d73c-5528-9cd4-02175de9d6fa", // avoid obstacles
      "cpt__8597053c-aa33-5a67-bfb1-5c7fda0000e8", // land
      "cpt__869da7e3-aafb-50fc-8357-e5610acf843b", // follow
      "cpt__1d513184-7846-5c4a-92f3-44b46f937585", // hold position
      "cpt__86968c51-6521-5cca-8f00-440a216ce713", // control flight
      "cpt__2afb018e-9cab-5fa6-9916-f8c2d397e614", // decide
      "cpt__dbb2632d-df65-5480-ad39-5e1f3c2275e4", // manoeuvre
      "cpt__8d061943-9d10-51d9-bc39-3b5871807eaa", // plan route
      "cpt__59e8f321-cab0-5308-8a91-ea8be5168316", // dock
      "cpt__e81ade56-1305-5b66-8934-a778915a16a6", // keep formation
    ],
  },
  {
    id: "protection",
    label: "Air & missile defence, protection",
    abilities: [
      "cpt__a172e0c3-6d95-5f86-8fe0-5ba572f8819c", // counter air threats
      "cpt__c2d384e6-7826-5529-8274-df5e14bfa341", // clear mines
      "cpt__34577fdf-7bc5-5577-ae8d-e7e04cf8f214", // patrol areas
      "cpt__5fe68d07-cbba-597a-8f06-df1f9293c990", // protect sites and people
      "cpt__e368186d-05fb-53c3-9842-614ace5fdde9", // reduce signature
      "cpt__0f6d6790-4cdf-5bd5-a003-d5fb05c800e0", // counter submarines
      "cpt__3204cc3e-0e82-544f-bef7-32b329ad789f", // protect wearer or equipment
    ],
  },
  {
    id: "platforms",
    label: "Platforms & mobility",
    abilities: [
      "cpt__44b27a1f-f8a8-562d-8317-8b38242b8e39", // fly
      "cpt__475e8971-b028-5ac9-ba12-86a3cc0dce39", // carry payload
      "cpt__26273a8d-c8c1-5ed8-b7da-9b3a8e2c4d8e", // transport cargo
      "cpt__bcbc5b13-7179-5e51-a7f8-5e8b33ad0bf4", // propel
      "cpt__bcc384c8-68e9-5c4c-9f75-2c959aea4fac", // loiter
      "cpt__9430bfcf-deff-5054-a5d3-66fa3da51a92", // take off
      "cpt__4f0127aa-e649-5d4a-beca-37a1325d2ae1", // move
      "cpt__565486a4-7fc8-56bc-ab3b-deb7969c3914", // recover vehicles
      "cpt__68f45dad-69fe-5fb6-8b3d-366530d1130a", // drive
      "cpt__28de14ef-5476-5d61-b89f-01136427a43b", // climb
      "cpt__9359345c-b580-5f10-862e-aa690bc3a95e", // dive underwater
    ],
  },
  {
    id: "c2",
    label: "C2 & communications",
    abilities: [
      "cpt__11075188-5e21-5cfd-94c8-1bfd10ab63af", // communicate
      "cpt__6c11b30f-04b6-5b8c-8c03-7ac292330911", // integrate
      "cpt__1e1db000-7e1a-57de-8d6d-4a3b524e3d75", // coordinate
      "cpt__a9a9566f-f8b1-54cb-8e58-2ed0fbc63c2f", // process data
      "cpt__8dd25d18-27e8-586d-b985-b42f16184d5b", // fuse data
      "cpt__90282e37-e46b-5c84-b474-cb1ae81cd34d", // acquire data
      "cpt__f66fee26-29fa-55c3-b6ef-7ba96753bc11", // plan
      "cpt__2358ef81-fa9b-5460-ab88-0334ef5f1e4e", // display
    ],
  },
];

// Every set the address may name
export const CAPABILITY_SETS: readonly string[] = [
  MOST_CLAIMED,
  ...CAPABILITY_PRESETS.map((preset) => preset.id),
  CUSTOM,
];

export const presetOf = (set: string): CapabilityPreset | undefined =>
  CAPABILITY_PRESETS.find((preset) => preset.id === set);
